'use client';

import { FileText, ArrowRight } from 'lucide-react';
// Imported from the small module, not lib/analytics: this component is copied
// verbatim into apps/blog, which cannot reach the 1,100-line file.
import { trackCtaClick, type CtaLocation } from '@/lib/analytics-cta';

/**
 * Software-quote CTA — routes knowledge-base readers to the matching product in
 * our own software store, where they can request a quote.
 *
 * These readers are already operating (or leaving) a product we resell through
 * distribution, so a quote is worth one to two orders of magnitude more than the
 * ad impression it replaces. See lib/data/software-quote-funnel.ts for the
 * traffic and the arithmetic.
 *
 * Bundle-safe: plain <a>, no next/link, so it can be imported anywhere including
 * R2-bundled tools. See CLAUDE.md "R2 Tool Bundle System".
 */

interface SoftwareQuoteCTAProps {
  /** Store product slug — resolves to /software/{slug}. */
  productSlug: string;
  /** Display name of the product, e.g. "CrowdStrike Falcon". */
  productName: string;
  /** Reader is leaving a product rather than operating it — changes the pitch. */
  switching?: boolean;
  /** The product being left, when switching. Used for honest framing. */
  leavingProduct?: string;
  /** What one unit is called (e.g. "endpoints") when the product is on the
   *  instant-quote fast path, from lib/pax8/catalog.ts. Present only for those
   *  products: the copy then promises an on-screen price, which the manual
   *  form cannot deliver. */
  instantQuoteUnit?: string;
  /** Source article slug, for utm_content and click tracking. */
  source: string;
  /** Tighter spacing for the sidebar. */
  compact?: boolean;
  /** Where the CTA is rendered, so KB and blog placements stay separable in
   *  GA4. Defaults to the knowledge base, which is where it started. */
  location?: CtaLocation;
  /** Overrides utm_medium, which otherwise follows `location`. */
  medium?: string;
}

/** Centralized destination builder — the single swap point for all quote CTAs. */
export function buildSoftwareQuoteUrl(
  productSlug: string,
  source: string,
  medium = 'kb_cta'
): string {
  const params = new URLSearchParams({
    utm_source: 'inventivehq',
    utm_medium: medium,
    utm_campaign: 'software-quote',
    utm_content: source,
  });
  return `/software/${productSlug}?${params.toString()}`;
}

export default function SoftwareQuoteCTA({
  productSlug,
  productName,
  switching = false,
  leavingProduct,
  instantQuoteUnit,
  source,
  compact,
  location = 'kb_cta',
  medium,
}: SoftwareQuoteCTAProps) {
  const href = buildSoftwareQuoteUrl(productSlug, source, medium ?? location);

  // Someone mid-uninstall is not a prospect for the thing they are removing.
  // Say what they are actually doing and offer the alternative plainly.
  const eyebrow = switching ? 'Moving off ' + (leavingProduct ?? 'your current tool') + '?' : 'Need licensing?';
  const heading = switching
    ? `Compare ${productName} before you decide`
    : instantQuoteUnit
      ? `See your ${productName} price now`
      : `Get ${productName} pricing`;
  const body = switching
    ? `If you are replacing ${leavingProduct ?? 'your current tool'}, we can quote ${productName} and walk through the migration — including what carries over and what does not. No obligation, and we will tell you if staying put is the better call.`
    : instantQuoteUnit
      ? `Pick a tier and how many ${instantQuoteUnit} you need — the price shows on screen and lands in your inbox. No call needed.`
      : `We resell ${productName} through distribution, so we can quote licensing, renewals and seat changes directly. Tell us your seat count and we will come back with real numbers rather than a "contact sales" form.`;

  return (
    <div
      className={`${compact ? 'p-4' : 'p-5'} rounded-lg border border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
            <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 mb-1">
            {eyebrow}
          </div>
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-1">{heading}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{body}</p>
          <a
            href={href}
            onClick={() => trackCtaClick(`SoftwareQuote: ${productSlug}`, location, href)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white px-4 py-2 rounded-md font-medium text-sm hover:shadow-lg transition-all"
          >
            Get my price
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
